// src/hooks/useRealtimeHealthProfile.ts
import { useEffect, useState, useCallback } from 'react';
import { supabase, isRealtimeEnabled } from '@/lib/supabase';
import { UserHealthProfile, RealtimePayload } from '@/types/database';

interface UseRealtimeHealthProfileOptions {
  userId: string | null;
  initialScore?: number | null;
  initialSummary?: string | null;
  onProfileUpdated?: (profile: UserHealthProfile) => void;
}

export const useRealtimeHealthProfile = ({
  userId,
  initialScore = null,
  initialSummary = null,
  onProfileUpdated,
}: UseRealtimeHealthProfileOptions) => {
  const [healthScore, setHealthScore] = useState<number | null>(initialScore);
  const [healthSummary, setHealthSummary] = useState<string | null>(initialSummary);
  const [isConnected, setIsConnected] = useState(false);

  useEffect(() => {
    setHealthScore(initialScore);
    setHealthSummary(initialSummary);
  }, [initialScore, initialSummary]);

  const handleProfileUpdate = useCallback(
    (payload: any) => {
      const updated = payload.new as UserHealthProfile;

      console.log('[Realtime] Profile updated:', {
        score: updated.overall_health_score,
        hasSummary: !!updated.health_summary,
      });

      setHealthScore(updated.overall_health_score);
      setHealthSummary(updated.health_summary);

      if (onProfileUpdated) {
        onProfileUpdated(updated);
      }
    },
    [onProfileUpdated]
  );

  useEffect(() => {
    if (!userId || !isRealtimeEnabled() || !supabase) {
      console.log('[Realtime] Skipping profile subscription - userId:', userId, 'enabled:', isRealtimeEnabled());
      return;
    }

    console.log('[Realtime] Setting up health profile subscription for user:', userId);

    const channel = supabase
      .channel('health-profile-updates')
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'user_health_profiles',
          filter: `user_id=eq.${userId}`,
        },
        (payload) => {
          console.log('[Realtime] Profile UPDATE event:', payload);
          handleProfileUpdate(payload);
        }
      )
      .subscribe((status) => {
        console.log('[Realtime] Profile channel status:', status);
        setIsConnected(status === 'SUBSCRIBED');
      });

    return () => {
      console.log('[Realtime] Cleaning up health profile subscription');
      if (supabase) {
        supabase.removeChannel(channel);
      }
      setIsConnected(false);
    };
  }, [userId, handleProfileUpdate]);

  return {
    healthScore,
    healthSummary,
    isConnected,
    setHealthScore,
    setHealthSummary,
  };
};
